
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { CheckCircle, AlertTriangle, Lightbulb } from 'lucide-react';

const CongratulationsProblemSection = () => {
  const { isMobile } = useBreakpoint();

  return (
    <SectionLayout 
      backgroundColor="bg-white" 
      paddingY="py-16 md:py-20 lg:py-24"
      containerWidth="wide"
    >
      <div className="text-center space-y-12 md:space-y-16">
        
        {/* Congratulations */}
        <div className="space-y-6 animate-fade-in">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/80 rounded-full shadow-lg border-2 border-[#FEE6D8]/50">
            <CheckCircle className="text-[#003a70]" size={isMobile ? 24 : 28} />
            <span className="text-[#003a70] font-bold text-lg md:text-xl">Congratulations!</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl xl:text-5xl"
          )}>
            You Now Have The Knowledge to Build 
            <br />
            <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
              An AI Business Card Service
            </span>
          </h2>
          
          <p className={cn(
            "text-[#003a70]/80 leading-relaxed max-w-3xl mx-auto",
            isMobile ? "text-lg" : "text-xl lg:text-2xl"
          )}>
            The Genius Card Playbook gives you everything you need to get started.
          </p>
        </div>

        {/* But Here's The Thing */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/20 via-white to-[#FEE6D8]/10 rounded-2xl p-8 md:p-12 border border-[#FEE6D8]/30 shadow-lg max-w-5xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#003a70]/10 rounded-full flex items-center justify-center">
                <Lightbulb className="text-[#003a70]" size={24} />
              </div>
            </div>
            
            <h3 className={cn(
              "font-bold text-[#003a70] mb-6 leading-tight",
              isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
            )}>
              But here's the thing...
            </h3>
            
            <p className={cn(
              "text-[#003a70]/90 leading-relaxed",
              isMobile ? "text-lg" : "text-xl lg:text-2xl"
            )}>
              <span className="font-bold text-[#003a70]">Knowledge without action is worthless.</span>
            </p>
          </div>
        </div>

        {/* What's About To Happen */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <h3 className={cn(
            "font-bold text-[#003a70] leading-tight",
            isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
          )}>
            And I know what's about to happen next.
          </h3>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-gray-200/60 shadow-lg max-w-5xl mx-auto">
            <div className="space-y-6 text-left">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#FEE6D8] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#003a70] font-bold text-sm">1</span>
                </div>
                <p className={cn(
                  "text-[#003a70]/90 leading-relaxed",
                  isMobile ? "text-base" : "text-lg"
                )}>
                  You're going to close this page and dive into the course
                </p>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#FEE6D8] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#003a70] font-bold text-sm">2</span>
                </div>
                <p className={cn(
                  "text-[#003a70]/90 leading-relaxed",
                  isMobile ? "text-base" : "text-lg"
                )}>
                  And then... get stuck.
                </p>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#FEE6D8] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#003a70] font-bold text-sm">3</span>
                </div>
                <p className={cn(
                  "text-[#003a70]/90 leading-relaxed",
                  isMobile ? "text-base" : "text-lg"
                )}>
                  Not because the strategy doesn't work. <span className="font-bold text-[#003a70]">It absolutely does.</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* The Problem */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="relative bg-white rounded-3xl p-8 md:p-12 border border-gray-200/60 shadow-[0_8px_30px_rgb(0,0,0,0.12)] max-w-5xl mx-auto overflow-hidden">
            {/* Subtle gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/40 via-transparent to-orange-50/30 pointer-events-none"></div>
            
            {/* Warning accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 to-orange-400"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                  <AlertTriangle className="text-white" size={26} />
                </div>
              </div>
              
              <h3 className={cn(
                "font-bold text-gray-900 mb-8 leading-tight",
                isMobile ? "text-lg md:text-xl" : "text-xl lg:text-2xl"
              )}>
                But because you're going to hit the same wall that stops most people:
              </h3>
              
              <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-2xl p-8 border-l-4 border-red-500">
                <p className={cn(
                  "text-gray-900 leading-relaxed font-bold text-center",
                  isMobile ? "text-xl" : "text-2xl lg:text-3xl"
                )}>
                  <span className="text-red-600">"Now I have to create all the sales materials..."</span>
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default CongratulationsProblemSection;
